// SP-4 WS-1 — the vendor "Contract terms" tab: structured, effective-dated
// compensation terms (the record the invoice contract check computes from).
// One arrangement at a time: adding terms supersedes the current row and
// stamps its end date — history stays visible below. Each basis explains
// itself with an example sentence so a first-time user can pick the right
// one; the free-text billing notes stay alongside as context, but the
// structured row is what checks the math.

import { useMemo, useState } from "react";
import {
  Button,
  Card,
  EmptyState,
  Input,
  Label,
  Skeleton,
  StatusBadge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { calendarDateInputToIso, formatCalendarDate } from "@/utils/datetime";
import { TermProposalSection } from "./TermProposalSection";
import {
  defaultTierRows,
  describeBand,
  makeTierRow,
  tierRowsToBands,
  type TierRowInput,
} from "./contractTermTierForm";
import {
  useAddVendorContractTerm,
  useVendorContractTerms,
} from "@/hooks/portfolioAdmin/useRegistries";
import type {
  VendorContractTermDto,
  VendorDto,
  VendorFeeBasis,
} from "@/api/portfolioAdmin/types";
import { fmtMoney } from "../billing/vendorBillingFormat";

const BASIS_LABEL: Record<VendorFeeBasis, string> = {
  FlatPerEngagement: "Flat fee per engagement",
  Hourly: "Hourly rate",
  PercentOfValue: "Percentage of value",
  Tiered: "Tiered rate table",
};

/** The self-explaining example sentence per basis, live from the typed
 *  amount ("2% of transaction value — e.g. a $10,000 engagement bills
 *  $200"). */
function exampleSentence(basis: VendorFeeBasis, amount: number | null): string {
  switch (basis) {
    case "FlatPerEngagement": {
      const a = amount ?? 450;
      return `A fixed fee each period — e.g. every engagement bills ${fmtMoney(a)}, regardless of hours or value.`;
    }
    case "Hourly": {
      const a = amount ?? 150;
      return `Rate × hours worked — e.g. 3 hours at ${fmtMoney(a)}/hr bills ${fmtMoney(a * 3)}.`;
    }
    case "PercentOfValue": {
      const pct = amount ?? 2;
      return `${pct}% of transaction value — e.g. a $10,000 engagement bills ${fmtMoney((pct / 100) * 10000)}.`;
    }
    case "Tiered":
      // The rule the signed compensation schedule states, in the words a
      // staff member needs before they type a table: the WHOLE amount bills
      // at the band's rate, not just the part above the threshold.
      return (
        "The volume for a period picks one band, and the whole amount bills at "
        + "that band's rate — e.g. with $0–$50,000 at 30% and $50,000–$100,000 "
        + `at 25%, a $75,000 period bills ${fmtMoney(18750)}, not ${fmtMoney(21250)}.`
      );
  }
}

function termSummary(term: VendorContractTermDto): string {
  switch (term.basis) {
    case "FlatPerEngagement":
      return `${fmtMoney(term.amount)} flat per engagement`;
    case "Hourly":
      return `${fmtMoney(term.amount)}/hr`;
    case "PercentOfValue":
      return `${(term.amount * 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}% of value`;
    case "Tiered": {
      const bands = term.tiers ?? [];
      return bands.length === 0
        ? "Tiered (no bands)"
        : `Tiered — ${bands.length} band${bands.length === 1 ? "" : "s"}`;
    }
  }
}

export function VendorContractTermsTab({ vendor }: { vendor: VendorDto }) {
  const auth = useAuth();
  const termsQuery = useVendorContractTerms(vendor.id);
  const terms = useMemo(() => termsQuery.data ?? [], [termsQuery.data]);
  const canEdit = auth.hasPermission("Vendors.Update") || auth.hasRoleCode("ADMIN");
  const [adding, setAdding] = useState(false);

  const current = terms.find((t) => t.effectiveTo === null) ?? null;

  return (
    <div className="flex flex-col gap-space-4" data-testid="vendor-contract-terms-tab">
      <Card>
        <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-3">
          <h3 className="font-display font-bold text-h3 m-0">Contract terms</h3>
          {canEdit && !adding && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setAdding(true)}
              data-testid="add-contract-term"
            >
              {current ? "Update terms" : "Add terms"}
            </Button>
          )}
        </div>

        <p className="text-small text-gray-mid m-0 mb-space-3">
          How this vendor is compensated. Every draft invoice is checked
          against the terms in effect for its billing period — a difference
          is flagged for review, never silently accepted.
        </p>

        {vendor.isRegulated && (
          <p
            className="text-small text-onyx m-0 mb-space-3 rounded-md border border-border-light bg-white-warm p-space-3"
            data-testid="regulated-consequence-note"
          >
            <span className="font-medium">This is a regulated vendor:</span>{" "}
            regulated vendors bill the borrower directly through Phoenix — the
            monthly invoice goes to the borrower&apos;s portal, not the
            vendor&apos;s.
          </p>
        )}

        {termsQuery.isLoading && <Skeleton className="h-16 w-full" />}
        {termsQuery.isError && (
          <p className="text-small text-status-error m-0" role="alert">
            Could not load the contract terms.
          </p>
        )}

        {termsQuery.isSuccess && !current && !adding && (
          <EmptyState
            variant="empty"
            title="No structured terms on file"
            description="Invoices from this vendor can't be checked automatically until terms are added. The free-text billing notes below still show for context."
          />
        )}

        {current && !adding && (
          <div
            className="rounded-md border border-status-success bg-status-success/10 p-space-3 flex flex-col gap-space-1"
            data-testid="current-term-card"
          >
            <div className="flex items-center gap-space-2 flex-wrap">
              <StatusBadge tone="ok">Current</StatusBadge>
              <span className="font-display font-semibold text-lead">
                {termSummary(current)}
              </span>
            </div>
            <p className="text-caption text-gray-mid m-0">
              {BASIS_LABEL[current.basis]} · effective{" "}
              {formatCalendarDate(current.effectiveFrom)}
            </p>
            <p className="text-small text-gray-mid m-0">
              {exampleSentence(
                current.basis,
                current.basis === "PercentOfValue"
                  ? current.amount * 100
                  : current.amount,
              )}
            </p>
            {current.basis === "Tiered" && <TierBandList term={current} />}
            {current.notes && (
              <p className="text-small m-0 whitespace-pre-wrap">{current.notes}</p>
            )}
          </div>
        )}

        {adding && (
          <AddTermForm
            vendorId={vendor.id}
            hasCurrent={current !== null}
            onDone={() => setAdding(false)}
          />
        )}
      </Card>

      {/* Step 6 W5 — proposals read off a countersigned schedule sit ABOVE
          the history: they are the thing that needs a decision. */}
      <TermProposalSection vendorId={vendor.id} canEdit={canEdit} current={current} />

      {terms.length > 0 && (
        <Card data-testid="terms-history">
          <h3 className="font-display font-bold text-h3 m-0 mb-space-3">History</h3>
          <Table>
            <THead>
              <TR>
                <TH className="text-white-pure">Terms</TH>
                <TH className="text-white-pure">Basis</TH>
                <TH className="text-white-pure">Effective from</TH>
                <TH className="text-white-pure">Effective to</TH>
                <TH className="text-white-pure">Notes</TH>
              </TR>
            </THead>
            <TBody>
              {terms.map((t) => (
                <TR key={t.id} data-testid={`term-row-${t.id}`}>
                  <TD className="whitespace-nowrap tabular-nums">
                    {termSummary(t)}
                    {t.basis === "Tiered" && <TierBandList term={t} />}
                  </TD>
                  <TD>{BASIS_LABEL[t.basis]}</TD>
                  <TD className="whitespace-nowrap">{formatCalendarDate(t.effectiveFrom)}</TD>
                  <TD className="whitespace-nowrap">
                    {t.effectiveTo ? (
                      formatCalendarDate(t.effectiveTo)
                    ) : (
                      <StatusBadge tone="ok">Current</StatusBadge>
                    )}
                  </TD>
                  <TD className="max-w-[16rem] truncate">{t.notes ?? "—"}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      <Card data-testid="billing-notes-card">
        <h3 className="font-display font-bold text-h3 m-0 mb-space-2">
          Billing notes (free text)
        </h3>
        {vendor.billingTermsNotes ? (
          <p className="text-small m-0 whitespace-pre-wrap">
            {vendor.billingTermsNotes}
          </p>
        ) : (
          <p className="text-small text-gray-mid italic m-0">
            No billing notes — edit the vendor record to add context. Notes are
            for humans; the structured terms above are what check the math.
          </p>
        )}
      </Card>
    </div>
  );
}

function AddTermForm({
  vendorId,
  hasCurrent,
  onDone,
}: {
  vendorId: string;
  hasCurrent: boolean;
  onDone: () => void;
}) {
  const toast = useToast();
  const addTerm = useAddVendorContractTerm(vendorId);
  const [basis, setBasis] = useState<VendorFeeBasis>("FlatPerEngagement");
  const [amountText, setAmountText] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [notes, setNotes] = useState("");
  const [tierRows, setTierRows] = useState<TierRowInput[]>(defaultTierRows);

  const tiered = basis === "Tiered";
  // Converted eagerly so the band error shows while typing rather than on
  // submit — the table is long enough that discovering a gap at the end is
  // a re-read of every row.
  const tierConversion = useMemo(() => tierRowsToBands(tierRows), [tierRows]);

  const amount = (() => {
    const n = Number(amountText.replace(/[$,%\s]/g, ""));
    return Number.isFinite(n) && amountText.trim() !== "" ? n : null;
  })();

  const amountLabel =
    basis === "Hourly"
      ? "Rate (USD per hour)"
      : basis === "PercentOfValue"
        ? "Percentage (e.g. 2 = 2%)"
        : "Fee (USD per engagement)";

  const valid = effectiveFrom !== "" && (tiered
    // A tiered term has no scalar amount — it prices from its bands.
    ? tierConversion.error === null
    : amount !== null && amount > 0 && (basis !== "PercentOfValue" || amount < 100));

  const save = async () => {
    if (!tiered && amount === null) return;
    if (tiered && tierConversion.error !== null) return;
    try {
      await addTerm.mutateAsync({
        basis,
        // Percent is entered as a whole number and stored as a fraction
        // (the TpsFeeScheduleModal ÷100 convention). A tiered term carries no
        // scalar amount; the server stores 0 regardless of what is sent.
        amount: tiered ? 0 : basis === "PercentOfValue" ? amount! / 100 : amount!,
        tiers: tiered ? tierConversion.bands : undefined,
        // Widen the bare date to UTC midnight so it round-trips stably.
        // Wall-calendar date (noon UTC) — an effective date must not
        // shift a day when re-rendered in the viewer's timezone.
        effectiveFrom: calendarDateInputToIso(effectiveFrom)!,
        notes: notes.trim() === "" ? null : notes.trim(),
      });
      toast.success("Contract terms saved.");
      onDone();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <div
      className="rounded-md border border-border-light p-space-3 flex flex-col gap-space-3"
      data-testid="add-term-form"
    >
      <div className="flex flex-col gap-space-1">
        <Label htmlFor="term-basis">How is this vendor paid?</Label>
        <select
          id="term-basis"
          className="rounded-md border border-border-light bg-white-pure px-space-2 py-space-1 text-small max-w-md"
          value={basis}
          onChange={(e) => setBasis(e.target.value as VendorFeeBasis)}
          data-testid="term-basis-select"
        >
          <option value="FlatPerEngagement">{BASIS_LABEL.FlatPerEngagement}</option>
          <option value="Hourly">{BASIS_LABEL.Hourly}</option>
          <option value="PercentOfValue">{BASIS_LABEL.PercentOfValue}</option>
          <option value="Tiered">{BASIS_LABEL.Tiered}</option>
        </select>
        <p className="text-caption text-gray-mid m-0" data-testid="basis-example">
          {exampleSentence(basis, amount)}
        </p>
      </div>

      {tiered && (
        <TierTableEditor
          rows={tierRows}
          onChange={setTierRows}
          error={tierConversion.error}
        />
      )}

      <div className="flex gap-space-3 flex-wrap">
        {!tiered && (
          <div className="flex flex-col gap-space-1 w-52">
            <Label htmlFor="term-amount">{amountLabel}</Label>
            <Input
              id="term-amount"
              inputMode="decimal"
              value={amountText}
              onChange={(e) => setAmountText(e.target.value)}
              placeholder={basis === "PercentOfValue" ? "2" : "0.00"}
              className="text-right tabular-nums"
              data-testid="term-amount-input"
            />
          </div>
        )}
        <div className="flex flex-col gap-space-1 w-52">
          <Label htmlFor="term-effective-from">Effective from</Label>
          <Input
            id="term-effective-from"
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            data-testid="term-effective-from-input"
          />
        </div>
      </div>

      <div className="flex flex-col gap-space-1">
        <Label htmlFor="term-notes">Notes (optional)</Label>
        <Input
          id="term-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. per the signed engagement letter of May 2026"
          data-testid="term-notes-input"
        />
      </div>

      {hasCurrent && (
        <p className="text-caption text-gray-mid m-0" data-testid="supersede-note">
          Saving replaces the current terms from the effective date — the old
          terms stay in the history, and invoices for earlier periods are
          still checked against them.
        </p>
      )}

      <div className="flex gap-space-2">
        <Button
          variant="primary"
          size="sm"
          disabled={!valid || addTerm.isPending}
          onClick={() => void save()}
          data-testid="save-term"
        >
          {addTerm.isPending ? "Saving…" : "Save terms"}
        </Button>
        <Button variant="secondary" size="sm" onClick={onDone} disabled={addTerm.isPending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * Step 6 W4 — the band table on Update Terms.
 *
 * Shaped to match the signed compensation schedule's own editor: an
 * "up to $X per period" ceiling per row, the last row open-ended. Staff read
 * the schedule and key the terms, so the two surfaces showing the same table
 * two different ways is how a transcription error gets made.
 */
function TierTableEditor({
  rows,
  onChange,
  error,
}: {
  rows: TierRowInput[];
  onChange: (rows: TierRowInput[]) => void;
  error: string | null;
}) {
  const update = (index: number, patch: Partial<TierRowInput>) =>
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const add = () => onChange([...rows, makeTierRow()]);

  const remove = (index: number) => {
    if (rows.length <= 1) return;
    const next = rows.filter((_, i) => i !== index);
    // Whatever is now last becomes the open-ended top band, so its ceiling
    // is cleared rather than left as a value the form no longer reads.
    next[next.length - 1] = { ...next[next.length - 1], thresholdText: "" };
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-space-2" data-testid="tier-table-editor">
      <p className="text-caption text-gray-mid m-0">
        One band per row, lowest first. Each band starts where the previous one
        ended, and the last band is open-ended so nothing falls off the top of
        the table.
      </p>
      <ul className="divide-y divide-border-light border border-border-light rounded-md m-0 p-0 list-none">
        {rows.map((row, index) => {
          const isTop = index === rows.length - 1;
          return (
            <li key={index} className="flex flex-col gap-space-2 p-space-3">
              <div className="flex items-center justify-between gap-space-2">
                <span className="text-caption font-medium text-gray-mid">
                  {isTop ? `Band ${index + 1} — everything above the previous band` : `Band ${index + 1}`}
                </span>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => remove(index)}
                  disabled={rows.length <= 1}
                  data-testid={`remove-tier-${index}`}
                >
                  Remove
                </Button>
              </div>
              <div className="flex gap-space-3 flex-wrap">
                {!isTop && (
                  <div className="flex flex-col gap-space-1 w-52">
                    <Label htmlFor={`tier-threshold-${index}`}>Up to ($ per period)</Label>
                    <Input
                      id={`tier-threshold-${index}`}
                      inputMode="decimal"
                      value={row.thresholdText}
                      onChange={(e) => update(index, { thresholdText: e.target.value })}
                      placeholder="50000"
                      className="text-right tabular-nums"
                      data-testid={`tier-threshold-${index}`}
                    />
                  </div>
                )}
                <div className="flex flex-col gap-space-1 w-40">
                  <Label htmlFor={`tier-rate-type-${index}`}>Rate type</Label>
                  <select
                    id={`tier-rate-type-${index}`}
                    className="rounded-md border border-border-light bg-white-pure px-space-2 py-space-1 text-small"
                    value={row.rateType}
                    onChange={(e) =>
                      update(index, { rateType: e.target.value as TierRowInput["rateType"] })
                    }
                    data-testid={`tier-rate-type-${index}`}
                  >
                    <option value="Percent">Percentage</option>
                    <option value="Flat">Flat amount</option>
                  </select>
                </div>
                <div className="flex flex-col gap-space-1 w-40">
                  <Label htmlFor={`tier-rate-${index}`}>
                    {row.rateType === "Percent" ? "Rate (e.g. 30 = 30%)" : "Rate ($ per period)"}
                  </Label>
                  <Input
                    id={`tier-rate-${index}`}
                    inputMode="decimal"
                    value={row.rateText}
                    onChange={(e) => update(index, { rateText: e.target.value })}
                    placeholder={row.rateType === "Percent" ? "30" : "1500"}
                    className="text-right tabular-nums"
                    data-testid={`tier-rate-${index}`}
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <div>
        <Button variant="secondary" size="sm" onClick={add} data-testid="add-tier">
          Add band
        </Button>
      </div>
      {error && (
        <p className="text-small text-status-error m-0" role="alert" data-testid="tier-error">
          {error}
        </p>
      )}
    </div>
  );
}

/** The bands of a tiered term, read-only. */
export function TierBandList({ term }: { term: VendorContractTermDto }) {
  const bands = term.tiers ?? [];
  if (bands.length === 0) return null;
  return (
    <ul className="m-0 mt-space-1 pl-space-4 text-small text-onyx" data-testid="term-band-list">
      {[...bands]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((b) => (
          <li key={b.id}>{describeBand(b)}</li>
        ))}
    </ul>
  );
}
